import React, { useState, useEffect } from "react";
import TopNavigation from "components/ui/TopNavigation";
import Icon from "components/AppIcon";
import BranchSetup from "./components/BranchSetup";
import PaymentTracker from "./components/PaymentTracker";
import StaffRoster from "./components/StaffRoster";
import ExpenseTracker from "./components/ExpenseTracker";
import ExpenseCategories from "./components/ExpenseCategories";
import ExpenseSummaryDashboard from "./components/ExpenseSummaryDashboard";
import PayrollManagement from "./components/PayrollManagement";
import RenewalAlerts from "./components/RenewalAlerts";
import AttendanceStats from "./components/AttendanceStats";
import LiveCheckInFeed from "./components/LiveCheckInFeed";
import OwnerWorkoutPlanBuilder from "./components/OwnerWorkoutPlanBuilder";
import OwnerDietPlanManager from "./components/OwnerDietPlanManager";
import EquipmentInventory from "./components/EquipmentInventory";
import PlanUsageSummary from "./components/PlanUsageSummary";
import OwnerAttendanceManager from "./components/OwnerAttendanceManager";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useBranch } from "../../contexts/BranchContext";
import { useSubscriptionLimits } from "../../hooks/useSubscriptionLimits";
import { useNavigate, useSearchParams } from "react-router-dom";

const tabs = [
  { key: "overview", label: "Overview", icon: "LayoutDashboard" },
  { key: "payments", label: "Payments", icon: "CreditCard" },
  { key: "staff", label: "Staff", icon: "UserCheck" },
  { key: "expenses", label: "Expenses", icon: "Receipt" },
  { key: "payroll", label: "Payroll", icon: "Banknote" },
  { key: "workout_plans", label: "Workout Plans", icon: "Dumbbell" },
  { key: "diet_plans", label: "Diet Plans", icon: "UtensilsCrossed" },
  { key: "attendance", label: "Attendance", icon: "UserCheck" },
  { key: "equipment", label: "Equipment", icon: "Dumbbell" },
];

export default function GymOwnerPortal() {
  const { user, signOut } = useAuth();
  const { branches, selectedBranchId } = useBranch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const activeExpenseTab = searchParams.get("expenseTab") || "tracker";
  const [kpis, setKpis] = useState({
    totalMembers: 0,
    monthlyRevenue: 0,
    pendingPayments: 0,
    activeStaff: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { subscription } = useSubscriptionLimits(user?.id);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      navigate('/login-page');
    } catch (err) {
      setLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const fetchKPIs = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const branchParam = selectedBranchId ? { branch_id: selectedBranchId } : {};

      const [memberRes, paymentRes, staffRes] = await Promise.all([
        api.get('/members', { params: { member_status: 'active', ...branchParam } }).catch(() => ({ data: [] })),
        api.get('/payments', { params: { ...branchParam } }).catch(() => ({ data: [] })),
        api.get('/staff', { params: { ...branchParam } }).catch(() => ({ data: [] })),
      ]);

      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      const monthlyPayments = (paymentRes.data || []).filter(p =>
        p?.payment_status === 'paid' &&
        p?.paid_date &&
        new Date(p?.paid_date) >= startOfMonth
      );
      const monthlyRevenue = monthlyPayments.reduce((sum, p) => sum + (parseFloat(p?.paid_amount) || 0), 0);

      const pendingCount = (paymentRes.data || []).filter(p =>
        ['pending', 'partial', 'overdue'].includes(p?.payment_status)
      ).length;

      const activeStaff = (staffRes.data || []).filter(s => s?.is_active !== false).length;

      setKpis({
        totalMembers: (memberRes.data || []).length,
        monthlyRevenue,
        pendingPayments: pendingCount,
        activeStaff,
      });
    } catch (err) {
      console.error('Error fetching KPIs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKPIs();
  }, [user?.id, selectedBranchId]);

  // Redirect vendors who haven't completed gym setup
  useEffect(() => {
    if (!user?.id || user?.role !== 'vendor') return;
    api.get('/gym-settings')
      .then(({ data }) => {
        if (!data || !data.setup_completed) navigate('/gym-setup', { replace: true });
      })
      .catch(() => navigate('/gym-setup', { replace: true }));
  }, [user?.id]);

  const ownerKPIs = [
    { label: "Total Members", value: loading ? "..." : String(kpis?.totalMembers), icon: "Users", color: "var(--color-primary)", change: "active" },
    { label: "Monthly Revenue", value: loading ? "..." : `$${kpis?.monthlyRevenue?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, icon: "DollarSign", color: "var(--color-accent)", change: "this month" },
    { label: "Pending Payments", value: loading ? "..." : String(kpis?.pendingPayments), icon: "Clock", color: "var(--color-warning)", change: "need attention" },
    { label: "Active Staff", value: loading ? "..." : String(kpis?.activeStaff), icon: "UserCheck", color: "var(--color-secondary)", change: !selectedBranchId ? `${branches?.length} branch${branches?.length !== 1 ? 'es' : ''}` : (branches?.find(b => b?.id === selectedBranchId)?.name || 'branch') },
  ];

  const trialBanner = (() => {
    if (user?.role !== 'vendor' || subscription?.payment_status !== 'trial') return null;
    if (!subscription?.end_date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(subscription.end_date);
    end.setHours(0, 0, 0, 0);
    const daysLeft = Math.round((end - today) / (1000 * 60 * 60 * 24));
    const trialDays = subscription?.trial_days || 14;
    if (daysLeft <= 0) return { expired: true, daysLeft: 0, trialDays };
    return { expired: false, daysLeft, trialDays };
  })();

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="pt-[0px]">
        {/* Trial Banner */}
        {trialBanner && (
          <div className={`px-4 md:px-6 py-2.5 ${trialBanner.expired ? 'bg-red-500/10 border-b border-red-500/20' : trialBanner.daysLeft <= 3 ? 'bg-amber-500/10 border-b border-amber-500/20' : 'bg-teal-500/10 border-b border-teal-500/20'}`}>
            <div className="max-w-screen-2xl mx-auto flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Icon
                  name={trialBanner.expired ? "AlertTriangle" : "Clock"}
                  size={15}
                  color={trialBanner.expired ? "var(--color-error)" : trialBanner.daysLeft <= 3 ? "#f59e0b" : "var(--color-primary)"}
                />
                <p className={`text-sm font-medium font-[var(--font-caption)] ${trialBanner.expired ? 'text-red-400' : trialBanner.daysLeft <= 3 ? 'text-amber-400' : 'text-teal-400'}`}>
                  {trialBanner.expired
                    ? `Your ${trialBanner.trialDays}-day free trial has ended. Upgrade to continue using all features.`
                    : `You're on a ${trialBanner.trialDays}-day free trial — ${trialBanner.daysLeft} day${trialBanner.daysLeft !== 1 ? 's' : ''} remaining.`}
                </p>
              </div>
              <button
                onClick={() => navigate('/vendor-plan-selection')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold font-[var(--font-caption)] rounded-lg whitespace-nowrap transition-colors ${trialBanner.expired ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-[var(--color-primary)] hover:bg-[var(--color-secondary)] text-white'}`}
              >
                <Icon name="Zap" size={12} color="white" />
                Upgrade Now
              </button>
            </div>
          </div>
        )}
        {/* Page Header */}
        <div className="bg-[var(--color-card)] border-b border-[var(--color-border)] px-4 md:px-6 py-4">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-md bg-[var(--color-primary)]/15 flex items-center justify-center">
                    <Icon name="Building2" size={15} color="var(--color-primary)" />
                  </div>
                  <h1 className="text-lg font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">Gym Owner Portal</h1>
                </div>
                <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Manage your gym operations, members, and staff</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/account-settings')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--color-border)] text-[var(--color-muted-foreground)] rounded-md hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
                >
                  <Icon name="Settings" size={13} color="currentColor" />
                  <span className="hidden sm:inline">Settings</span>
                </button>
                <button
                  onClick={() => setShowLogoutModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--color-error)]/30 text-[var(--color-error)] rounded-md hover:bg-[var(--color-error)]/5 hover:border-[var(--color-error)]/50 transition-colors"
                >
                  <Icon name="LogOut" size={13} color="var(--color-error)" />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            </div>

            {/* KPI Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {ownerKPIs?.map((kpi, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-[var(--color-muted)]/40 rounded-[var(--radius-sm)] border border-[var(--color-border)]">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${kpi?.color}18` }}>
                    <Icon name={kpi?.icon} size={15} color={kpi?.color} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] truncate">{kpi?.label}</p>
                    <p className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">{kpi?.value}</p>
                    <p className="text-xs font-[var(--font-caption)]" style={{ color: kpi?.color }}>{kpi?.change}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-[var(--color-card)] border-b border-[var(--color-border)] px-4 md:px-6">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
              {tabs?.map(tab => (
                <button key={tab?.key} onClick={() => setSearchParams({ tab: tab?.key })} className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium font-[var(--font-caption)] border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab?.key ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                }`}>
                  <Icon name={tab?.icon} size={14} color={activeTab === tab?.key ? "var(--color-primary)" : "currentColor"} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6">
          {activeTab === "overview" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 xl:grid-cols-1 gap-6">
                <div className="xl:col-span-2 space-y-6">
                  <BranchSetup onBranchChange={fetchKPIs} />
                </div>
                <div className="space-y-4">
                  <PlanUsageSummary />
                </div>
              </div>
              <AttendanceStats branchId={selectedBranchId} />
              <LiveCheckInFeed branchId={selectedBranchId} />
              <RenewalAlerts branchId={selectedBranchId} />
            </div>
          )}
          {activeTab === "payments" && (
            <PaymentTracker />
          )}
          {activeTab === "staff" && (
            <StaffRoster />
          )}
          {activeTab === "expenses" && (
            <div className="space-y-6">
              {/* Expense Sub-tabs */}
              <div className="flex items-center gap-1 border-b border-[var(--color-border)]">
                {[
                  { key: "tracker", label: "Expense Tracker", icon: "List" },
                  { key: "summary", label: "Summary Dashboard", icon: "BarChart2" },
                  { key: "categories", label: "Categories", icon: "Tag" },
                ]?.map(st => (
                  <button
                    key={st?.key}
                    onClick={() => setSearchParams({ tab: "expenses", expenseTab: st?.key })}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeExpenseTab === st?.key
                        ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                        : "border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                    }`}
                  >
                    <Icon name={st?.icon} size={14} color={activeExpenseTab === st?.key ? "var(--color-primary)" : "currentColor"} />
                    {st?.label}
                  </button>
                ))}
              </div>
              {activeExpenseTab === "tracker" && <ExpenseTracker onExpenseChange={() => {}} />}
              {activeExpenseTab === "summary" && <ExpenseSummaryDashboard />}
              {activeExpenseTab === "categories" && <ExpenseCategories onCategoriesChange={() => {}} />}
            </div>
          )}
          {activeTab === "payroll" && (
            <PayrollManagement />
          )}
          {activeTab === "workout_plans" && (
            <OwnerWorkoutPlanBuilder />
          )}
          {activeTab === "diet_plans" && (
            <OwnerDietPlanManager />
          )}
          {activeTab === "attendance" && (
            <OwnerAttendanceManager />
          )}
          {activeTab === "equipment" && (
            <EquipmentInventory />
          )}
        </div>
      </main>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !loggingOut && setShowLogoutModal(false)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] p-6 w-full max-w-sm">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center">
                <Icon name="LogOut" size={22} color="var(--color-error)" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Log Out?</h2>
                <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
                  Are you sure you want to log out?
                </p>
              </div>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  disabled={loggingOut}
                  className="flex-1 px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all font-[var(--font-caption)] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-error)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {loggingOut ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Logging out...</>
                  ) : (
                    <><Icon name="LogOut" size={14} color="#fff" />Log Out</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
